/**
 * Permission constants — mirrors backend PERMISSION_REGISTRY keys.
 * Use these instead of raw strings for compile-time safety.
 */

// Dashboard & Analytics
export const DASHBOARD_VIEW = 'dashboard.view';
export const DASHBOARD_EXPORT = 'dashboard.export';

// Constituency Management
export const CONSTITUENCIES_VIEW = 'constituencies.view';
export const CONSTITUENCIES_CREATE = 'constituencies.create';
export const CONSTITUENCIES_EDIT = 'constituencies.edit';
export const CONSTITUENCIES_DELETE = 'constituencies.delete';
export const CONSTITUENCIES_MANAGE_CUSTOM_FIELDS = 'constituencies.manage_custom_fields';

// Campaign Management
export const CAMPAIGNS_VIEW = 'campaigns.view';
export const CAMPAIGNS_CREATE = 'campaigns.create';
export const CAMPAIGNS_EDIT = 'campaigns.edit';
export const CAMPAIGNS_DELETE = 'campaigns.delete';

// Candidate Management
export const CANDIDATES_VIEW = 'candidates.view';
export const CANDIDATES_CREATE = 'candidates.create';
export const CANDIDATES_EDIT = 'candidates.edit';
export const CANDIDATES_DELETE = 'candidates.delete';

// Records
export const RECORDS_VIEW = 'records.view';
export const RECORDS_CREATE = 'records.create';
export const RECORDS_EDIT = 'records.edit';
export const RECORDS_DELETE = 'records.delete';
export const RECORDS_IMPORT = 'records.import';
export const RECORDS_EXPORT = 'records.export';
export const RECORDS_BULK_UPDATE = 'records.bulk_update';

// MP Module
export const MP_REQUESTS_VIEW = 'mp_requests.view';
export const MP_REQUESTS_CREATE = 'mp_requests.create';
export const MP_REQUESTS_EDIT = 'mp_requests.edit';
export const MP_REQUESTS_CLOSE = 'mp_requests.close';
export const MP_REQUESTS_DELETE = 'mp_requests.delete';
export const MP_REQUESTS_EXPORT = 'mp_requests.export';

// Call Centre
export const CALL_CENTRE_VIEW = 'call_centre.view';
export const CALL_CENTRE_MANAGE_CALLERS = 'call_centre.manage_callers';
export const CALL_CENTRE_ASSIGN_BLOCKS = 'call_centre.assign_blocks';
export const CALL_CENTRE_VIEW_PROGRESS = 'call_centre.view_progress';

// Voting Day
export const VOTING_VIEW_BOXES = 'voting.view_boxes';
export const VOTING_MANAGE_BOXES = 'voting.manage_boxes';
export const VOTING_CHECKIN = 'voting.checkin';
export const VOTING_ASSIGN_BOXES = 'voting.assign_boxes';
export const VOTING_VIEW_TURNOUT = 'voting.view_turnout';

// User & Role Management
export const USERS_VIEW = 'users.view';
export const USERS_CREATE = 'users.create';
export const USERS_EDIT = 'users.edit';
export const USERS_DELETE = 'users.delete';
export const USERS_MANAGE_ROLES = 'users.manage_roles';
export const USERS_ASSIGN_ROLES = 'users.assign_roles';
export const USERS_MANAGE_PERMISSIONS = 'users.manage_permissions';

// System & Settings
export const SETTINGS_VIEW = 'settings.view';
export const SETTINGS_EDIT = 'settings.edit';
export const SETTINGS_MANAGE_COLUMNS = 'settings.manage_columns';

// Audit & Monitoring
export const AUDIT_VIEW = 'audit.view';
export const AUDIT_EXPORT = 'audit.export';
export const NOTIFICATIONS_VIEW = 'notifications.view';
export const NOTIFICATIONS_MANAGE = 'notifications.manage';

// Navigation & UI
export const NAV_DASHBOARD = 'nav.dashboard';
export const NAV_CONSTITUENCIES = 'nav.constituencies';
export const NAV_CAMPAIGNS = 'nav.campaigns';
export const NAV_VOTING_BOXES = 'nav.voting_boxes';
export const NAV_MP_REQUESTS = 'nav.mp_requests';
export const NAV_CALL_CENTRE = 'nav.call_centre';
export const NAV_AUDIT_LOG = 'nav.audit_log';
export const NAV_USERS = 'nav.users';
export const NAV_SETTINGS = 'nav.settings';

// ---------------------------------------------------------------------------
// Full-access roles
// ---------------------------------------------------------------------------

/**
 * Roles that bypass every permission check. Mirrors backend
 * FULL_ACCESS_ROLE_SLUGS in utils/permissions.py.
 */
export const FULL_ACCESS_ROLES = ['super_admin'];

/**
 * True if the user carries the hidden system-admin flag. The backend strips
 * `is_system` from user payloads for anyone who is not one, so this is only
 * ever true for the account looking at its own /auth/me.
 */
export function isSystemAdmin(user) {
  return user?.is_system === true;
}

/** True if the user bypasses permission checks. */
export function hasFullAccess(user) {
  return isSystemAdmin(user) || FULL_ACCESS_ROLES.includes(user?.role_slug);
}

// ---------------------------------------------------------------------------
// Data scope — which constituencies/campaigns a user may act in
// ---------------------------------------------------------------------------

/** Full-access identities bypass scoping entirely (mirrors utils/scopes.py). */
export function hasFullScope(user) {
  return hasFullAccess(user);
}

/**
 * True when a user has been granted no constituencies at all. Scoping denies by
 * default, so this is an unscoped account rather than an empty database — the
 * UI should say "ask an admin for access", not "create your first constituency".
 */
export function hasNoScope(user) {
  if (!user || hasFullScope(user)) return false;
  return (user.scopes?.constituencies?.length ?? 0) === 0;
}
